import * as React from 'react';

import { cn } from '@/lib/utils';

export interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  isBlurred?: boolean; // For background blur
}

const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  ({ src, alt, className, isBlurred, ...props }, ref) => {
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState<Error | null>(null);

    const handleLoad = () => {
      setIsLoading(false);
    };

    const handleError = () => {
      setIsLoading(false);
      setError(new Error('Failed to load image'));
    };

    return (
      <div className={cn('relative', className)}>
        {/* Blurred Background */}
        {isBlurred && (
          <div
            className="absolute inset-0 bg-cover bg-center blur-sm"
            style={{
              backgroundImage: `url(${src})`,
            }}
          />
        )}

        {/* Loading placeholder */}
        {isLoading && (
          <div className="absolute inset-0 animate-pulse bg-muted" />
        )}

        {/* Actual Image */}
        <img
          ref={ref}
          src={src}
          alt={alt}
          className={cn(
            'relative h-full w-full object-cover transition-opacity duration-300',
            isLoading ? 'opacity-0' : 'opacity-100',
          )}
          onLoad={handleLoad}
          onError={handleError}
          {...props}
        />

        {/* Error message */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground">
            خطأ في تحميل الصورة
          </div>
        )}
      </div>
    );
  },
);

Image.displayName = 'Image';

export { Image };
